import DOMPurify from 'dompurify';

/**
 * Sanitization configuration for different input types
 */
const SANITIZATION_CONFIG = {
  // Strict sanitization for user-generated content
  strict: {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
    KEEP_CONTENT: true,
    RETURN_DOM: false,
    RETURN_DOM_FRAGMENT: false,
    RETURN_DOM_IMPORT: false
  },
  
  // Basic sanitization for text fields (allows basic formatting)
  basic: {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'br'],
    ALLOWED_ATTR: [],
    KEEP_CONTENT: true,
    RETURN_DOM: false,
    RETURN_DOM_FRAGMENT: false,
    RETURN_DOM_IMPORT: false
  },
  
  // HTML sanitization for rich text content (admin only)
  html: {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'b', 'i', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
    ALLOWED_ATTR: ['class'],
    KEEP_CONTENT: true,
    RETURN_DOM: false,
    RETURN_DOM_FRAGMENT: false,
    RETURN_DOM_IMPORT: false
  }
};

/**
 * Sanitize text input based on type
 * @param {string} input - The input string to sanitize
 * @param {string} type - The type of sanitization ('strict', 'basic', 'html')
 * @returns {string} - Sanitized string
 */
export function sanitizeInput(input, type = 'strict') {
  if (typeof input !== 'string') {
    return input;
  }
  
  const config = SANITIZATION_CONFIG[type] || SANITIZATION_CONFIG.strict;
  return DOMPurify.sanitize(input, config);
}

/**
 * Sanitize object properties recursively
 * @param {Object} obj - Object to sanitize
 * @param {Object} fieldConfig - Configuration for each field
 * @returns {Object} - Sanitized object
 */
export function sanitizeObject(obj, fieldConfig = {}) {
  if (!obj || typeof obj !== 'object') {
    return obj;
  }
  
  const sanitized = Array.isArray(obj) ? [] : {};
  
  for (const [key, value] of Object.entries(obj)) {
    const config = fieldConfig[key] || { type: 'strict' };
    
    if (typeof value === 'string') {
      sanitized[key] = sanitizeInput(value, config.type);
    } else if (Array.isArray(value)) {
      sanitized[key] = value.map(item => 
        typeof item === 'string' ? sanitizeInput(item, config.type) : item
      );
    } else if (value && typeof value === 'object') {
      sanitized[key] = sanitizeObject(value, config.nested || {});
    } else {
      sanitized[key] = value;
    }
  }
  
  return sanitized;
}

/**
 * Escape HTML entities for output encoding
 * @param {string} str - String to escape
 * @returns {string} - HTML-escaped string
 */
export function escapeHtml(str) {
  if (typeof str !== 'string') {
    return str;
  }
  
  const htmlEscapes = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#x27;',
    '/': '&#x2F;'
  };
  
  return str.replace(/[&<>"'/]/g, match => htmlEscapes[match]);
}

/**
 * Validate and sanitize phone number
 * @param {string} phone - Phone number to validate
 * @returns {string|null} - Sanitized phone number or null if invalid
 */
export function sanitizePhone(phone) {
  if (typeof phone !== 'string') {
    return null;
  }
  
  // Remove all non-digit characters
  const cleaned = phone.replace(/\D/g, '');
  
  // Validate Israeli phone number format (10 digits starting with 05)
  if (cleaned.length === 10 && cleaned.startsWith('05')) {
    return cleaned;
  }
  
  return null;
}

/**
 * Validate and sanitize email
 * @param {string} email - Email to validate
 * @returns {string|null} - Sanitized email or null if invalid
 */
export function sanitizeEmail(email) {
  if (typeof email !== 'string') {
    return null;
  }
  
  const sanitized = sanitizeInput(email.trim().toLowerCase(), 'strict');
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  
  if (emailRegex.test(sanitized)) {
    return sanitized;
  }
  
  return null;
}

/**
 * Sanitize search query
 * @param {string} query - Search query to sanitize
 * @returns {string} - Sanitized search query
 */
export function sanitizeSearchQuery(query) {
  if (typeof query !== 'string') {
    return '';
  }
  
  // Remove potentially dangerous characters and limit length
  return sanitizeInput(query, 'strict')
    .replace(/[<>{}[\]\\^$|*+?.()]/g, '')
    .substring(0, 100);
}

/**
 * Sanitize form data before submission
 * @param {Object} formData - Form data to sanitize
 * @param {string} formType - Type of form for configuration
 * @returns {Object} - Sanitized form data
 */
export function sanitizeFormData(formData, formType) {
  const formConfigs = {
    // Driver registration form
    'driver-register': {
      businessName: { type: 'strict' },
      contactPerson: { type: 'strict' },
      businessLicense: { type: 'strict' },
      serviceAreas: { type: 'strict' }
    },
    
    // Contact form
    'contact': {
      name: { type: 'strict' },
      subject: { type: 'strict' },
      message: { type: 'basic' }
    },
    
    // Request form
    'request': {
      customerName: { type: 'strict' },
      originAddress: { type: 'strict' },
      destinationAddress: { type: 'strict' },
      additionalNotes: { type: 'basic' },
      heavyFurniture: { type: 'strict' },
      electricalItems: { type: 'strict' }
    },
    
    // Driver profile form
    'driver-profile': {
      businessName: { type: 'strict' },
      contactPerson: { type: 'strict' },
      businessLicense: { type: 'strict' },
      serviceAreas: { type: 'strict' }
    },
    
    // Offer form
    'offer': {
      notes: { type: 'basic' }
    }
  };
  
  const config = formConfigs[formType] || {};
  return sanitizeObject(formData, config);
}

/**
 * Sanitize text for display (prevents XSS)
 * @param {string} text - Text to sanitize for display
 * @returns {string} - Sanitized text safe for display
 */
export function sanitizeForDisplay(text) {
  if (typeof text !== 'string') {
    return text;
  }
  
  return sanitizeInput(text, 'strict');
}

/**
 * Sanitize HTML content for display (allows basic formatting)
 * @param {string} html - HTML content to sanitize
 * @returns {string} - Sanitized HTML safe for display
 */
export function sanitizeHtmlForDisplay(html) {
  if (typeof html !== 'string') {
    return html;
  }
  
  return sanitizeInput(html, 'basic');
}

const sanitizerUtils = {
  sanitizeInput,
  sanitizeObject,
  escapeHtml,
  sanitizePhone,
  sanitizeEmail,
  sanitizeSearchQuery,
  sanitizeFormData,
  sanitizeForDisplay,
  sanitizeHtmlForDisplay,
  SANITIZATION_CONFIG
};

export default sanitizerUtils;
